/**
 * ViewMapSetModal
 *
 * Modal for viewing and managing the case map set (endpoints marked for visualisation).
 * Thin wrapper around EndpointSetModal with map-set specific copy and visuals.
 */

import type { ReactElement } from 'react'
import { MapPin } from 'lucide-react'
import type { CaseEndpoint } from '../../types/case-container'
import { EndpointSetModal } from './EndpointSetModal'
import type { EndpointSetCopy, EndpointSetVisuals } from './endpoint-set-modal.parts'

interface ViewMapSetModalProps {
  mapSetIds: Set<string>
  endpoints: CaseEndpoint[]
  onClose: () => void
  onRemove: (endpointId: string) => void
  onRemoveMany: (endpointIds: string[]) => void
  onClear: () => void
}

const VISUALS: EndpointSetVisuals = {
  icon: MapPin,
  iconBgClass: 'bg-fuchsia-100 dark:bg-fuchsia-950/50',
  iconColorClass: 'text-fuchsia-600 dark:text-fuchsia-400',
}

const COPY: EndpointSetCopy = {
  titleKey: 'infrastructure.mapSet.title',
  endpointCountKey: 'infrastructure.mapSet.endpointCount',
  noSearchResultsKey: 'infrastructure.mapSet.noSearchResults',
  noEndpointsYetKey: 'infrastructure.mapSet.noEndpointsYet',
  addHintKey: 'infrastructure.mapSet.addHint',
  selectAllKey: 'infrastructure.mapSet.selectAll',
  showingCountKey: 'infrastructure.mapSet.showingCount',
  filteredKey: 'infrastructure.mapSet.filtered',
  clearAllConfirmKey: 'infrastructure.mapSet.clearAllConfirm',
  yesClearKey: 'infrastructure.mapSet.yesClear',
  clearAllKey: 'infrastructure.mapSet.clearAll',
  removeTooltipKey: 'infrastructure.removeFromMap',
}

export function ViewMapSetModal({
  mapSetIds,
  endpoints,
  onClose,
  onRemove,
  onRemoveMany,
  onClear,
}: ViewMapSetModalProps): ReactElement {
  return (
    <EndpointSetModal
      endpointIds={mapSetIds}
      endpoints={endpoints}
      visuals={VISUALS}
      copy={COPY}
      onClose={onClose}
      onRemove={onRemove}
      onRemoveMany={onRemoveMany}
      onClear={onClear}
    />
  )
}
